import { Injectable } from '@angular/core';
import {
    IChatConversation,
    IChatConversationMessage,
    IChatConversationUser,
    IChatSuggestion,
} from '@fi-shared/fi-chat/fi-chat';
import { FiChatConversationType } from '@fi-shared/fi-chat/fi-chat-conversation-type';
import { FiChatConversationMessageType } from '@fi-shared/fi-chat/fi-chat-conversation-message-type';
import { cloneDeep, isDefined, isEqual } from '@fi-shared/utils';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { AppTextService } from '@fi-shared/app-text';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';
import { AccessRightType, ApplicationSettingsType, PersonalNotificationsCategoryType } from '@fi-shared/types';
import { ILightWeightUser } from '@fi-shared/models';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiChatConversationRenameDialogComponent } from '@fi-shared/fi-chat/fi-chat-conversation/fi-chat-conversation-rename-dialog/fi-chat-conversation-rename-dialog.component';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { AccessToApplicationTypeService } from '@fi-shared/fi-shared-services/access-to-application-type.service';
import { FiChatSyncService } from '@fi-shared/fi-chat/fi-chat-sync.service';
import { FiLogErrorService } from '@fi-shared/fi-shared-services/fi-log-error.service';
import { union } from '@fi-shared/utils/fi-union';

const MSPerDay = 1000 * 60 * 60 * 24;
const MSPerHour = 1000 * 60 * 60;

interface IForwardedMessage {
    targetConversationId: number;
    targetUser?: ILightWeightUser;
    message: IChatConversationMessage;
}
@Injectable()
export class FiChatHelperService {
    private _forwardedMessage$ = new BehaviorSubject<IForwardedMessage>(undefined);
    private _openForwardMessageConversation$ = new Subject<IChatConversation>();
    private _showForwardMessageNewConversation$ = new Subject<IForwardedMessage>();
    //region conversation suggestions lazy-loading
    private readonly _suggestionsOffsetStep: number = 15;
    private _visibleUnitSuggestionLimit: number = this._suggestionsOffsetStep;
    private _visibleOtherSuggestionLimit: number = this._suggestionsOffsetStep;

    constructor(
        private userDataService: FiUserDataService,
        private appTextService: AppTextService,
        private fiChatSyncService: FiChatSyncService,
        private customerDataService: FiCustomerDataService,
        private fiDialogsService: FiDialogsService,
        private sanitizer: DomSanitizer,
        private accessToApplicationTypeService: AccessToApplicationTypeService,
        private fiLogErrorService: FiLogErrorService,
    ) {}

    /* determines if conversation list opened */
    private _showConversationList$ = new BehaviorSubject<boolean>(false);

    get showConversationList$() {
        return this._showConversationList$.asObservable();
    }

    /* count of currently rendered conversation cards in fi-chat */
    private _activeConversationsCount$ = new BehaviorSubject<number>(0);

    get activeConversationsCount$() {
        return this._activeConversationsCount$.asObservable();
    }

    setActiveConversationsCount(count: number) {
        if (this._activeConversationsCount$.value !== count) this._activeConversationsCount$.next(count);
    }

    /**
     * Width occupied by fi-chat from the right edge of the container, in pixels.
     * Mirrors the slot math in fi-chat.component.ts so the AI chat (and other
     * future floating panels) can position themselves to the left of fi-chat.
     */
    private _chatZoneWidth$: Observable<number>;
    get chatZoneWidth$(): Observable<number> {
        if (!this._chatZoneWidth$) {
            this._chatZoneWidth$ = combineLatest([this._showConversationList$, this._activeConversationsCount$]).pipe(
                map(([showList, n]) => FiChatHelperService.calcChatZoneWidth(showList, n)),
                distinctUntilChanged(),
                shareReplay(1),
            );
        }
        return this._chatZoneWidth$;
    }

    // Slot math mirrors fi-chat.component.ts (`getConversationContainerRightOffset`) and the SCSS widths.
    // Visual layout:
    //   • `.fi-chat` is `position:absolute; right:0; margin-right:25px; width:340px`
    //     -> its right edge sits 25px from #content's right edge.
    //   • Conversation list inside `.fi-chat` is 340px wide (fi-chat-conversation-list-container scss).
    //   • Each conversation card inside `.fi-chat` is 360px wide (fi-chat-conversation-container scss).
    //   • Cards are positioned `right:` relative to `.fi-chat`'s right edge via inline style.
    // We expose the leftmost edge of fi-chat content from #content's right edge — that's the
    // anchor point AI chat panel/button align to.
    private static readonly FI_CHAT_MARGIN_RIGHT = 25;
    private static readonly FI_CHAT_LIST_WIDTH = 340;
    private static readonly FI_CHAT_CONV_CARD_WIDTH = 360;
    private static readonly FI_CHAT_WRAPPER_WIDTH = 365; // .fi-chat width + margin (offsetWidth + marginRight in fi-chat.component.ts)
    private static readonly FI_CHAT_CONV_ADDITIONAL_WIDTH = 15; // CONVERSATION_ADDITIONAL_WIDTH
    private static readonly FI_CHAT_CONV_MARGIN_RIGHT_OFFSET = 100; // CONVERSATION_MARGIN_RIGHT (offset adj. when list is hidden)

    private static calcChatZoneWidth(showList: boolean, n: number): number {
        if (n === 0) {
            return showList ? FiChatHelperService.FI_CHAT_MARGIN_RIGHT + FiChatHelperService.FI_CHAT_LIST_WIDTH : 0;
        }
        // Inline `right` of the leftmost conversation card (highest container id) within `.fi-chat`.
        let rightOffset =
            FiChatHelperService.FI_CHAT_WRAPPER_WIDTH * n + FiChatHelperService.FI_CHAT_CONV_ADDITIONAL_WIDTH * (n - 1);
        if (!showList) {
            rightOffset +=
                -FiChatHelperService.FI_CHAT_WRAPPER_WIDTH + FiChatHelperService.FI_CHAT_CONV_MARGIN_RIGHT_OFFSET;
        }
        // Translate from `.fi-chat`-relative to #content-relative, then add card width.
        return rightOffset + FiChatHelperService.FI_CHAT_MARGIN_RIGHT + FiChatHelperService.FI_CHAT_CONV_CARD_WIDTH;
    }

    private _showForwardMessageConversationList$ = new Subject<void>();

    get showForwardMessageConversationList$() {
        return this._showForwardMessageConversationList$.asObservable();
    }

    get forwardMessage$() {
        return this._forwardedMessage$.asObservable();
    }

    private _closeForwardMessageConversationList$ = new Subject<void>();

    get closeForwardMessageConversationList$() {
        return this._closeForwardMessageConversationList$.asObservable();
    }

    get showForwardMessageConversation$(): Observable<IChatConversation> {
        return this._openForwardMessageConversation$.asObservable();
    }

    get showForwardMessageNewConversation$(): Observable<IForwardedMessage> {
        return this._showForwardMessageNewConversation$.asObservable();
    }

    //region open/close conversations list
    private _onConversationListOpening$ = new Subject<void>();

    get onConversationListOpening$() {
        return this._onConversationListOpening$.asObservable();
    }

    public get visibleSuggestionLimit(): number {
        return this._visibleUnitSuggestionLimit;
    }

    openConversationList() {
        this._showConversationList$.next(true);
    }

    closeConversationList() {
        this._showConversationList$.next(false);
    }

    isConversationListOpened() {
        return this._showConversationList$.value;
    }

    openForwardMessageConversationList(conversationMessage: IChatConversationMessage) {
        this._showForwardMessageConversationList$.next();
        this._forwardedMessage$.next({ targetConversationId: undefined, message: conversationMessage });
    }

    closeForwardMessageConversationList() {
        this._closeForwardMessageConversationList$.next();
        this._forwardedMessage$.next(undefined);
    }

    openForwardMessageConversation(conversation: IChatConversation) {
        const currentForwardMessage: {
            targetConversationId: number;
            message: IChatConversationMessage;
        } = this._forwardedMessage$.value;
        currentForwardMessage.targetConversationId = conversation.Id;
        this._forwardedMessage$.next(currentForwardMessage);
        this._openForwardMessageConversation$.next(conversation);
    }

    openForwardMessageNewConversation(targetUser: ILightWeightUser) {
        const currentForwardMessage: IForwardedMessage = this._forwardedMessage$.value;
        currentForwardMessage.targetUser = targetUser;
        this._forwardedMessage$.next(currentForwardMessage);
        this._showForwardMessageNewConversation$.next(currentForwardMessage);
    }

    //endregion

    isChatSupported(): boolean {
        return this.customerDataService.isChatSupported();
    }

    hasAccessToChat() {
        return this.accessToApplicationTypeService.HasAccessToApplicationType(
            ApplicationSettingsType.Chat,
            AccessRightType.Read,
        );
    }

    checkIfChatIsSupported(): boolean {
        const isChatSupported = this.customerDataService.isChatSupported();
        if (!isChatSupported) {
            this.fiDialogsService.notify({
                headerText: this.appTextService.get('chat.cant_open'),
                messageText: this.appTextService.get('chat.ios_too_old'),
            });
            return false;
        }
        return true;
    }

    raiseOpenConversationList() {
        if (this.checkIfChatIsSupported()) this._onConversationListOpening$.next();
    }

    findConversationBySelectedUserIds(selectedUserIds: number[]): IChatConversation {
        // https://chainformation.atlassian.net/browse/FI-6792
        // find existing conversation both in private and groups
        let currentConversation = this.findOneToOneConversation(selectedUserIds);
        if (!isDefined(currentConversation)) {
            currentConversation = this.findGroupConversation(selectedUserIds);
        }
        return currentConversation;
    }

    /**
     * find 1-1 conversation with participants: current user, selected user
     */
    findOneToOneConversation(selectedUserIds: number[]): IChatConversation {
        if (selectedUserIds.length !== 1) return undefined;
        return this.findConversationInternal(selectedUserIds, FiChatConversationType.OneToOne);
    }

    /**
     * find group conversation with participants: current user, selected users
     */
    findGroupConversation(selectedUserIds: number[]): IChatConversation {
        if (selectedUserIds.length === 0) return undefined;
        return this.findConversationInternal(selectedUserIds, FiChatConversationType.Group);
    }

    private findConversationInternal(
        selectedUserIds: number[],
        conversationType: FiChatConversationType,
    ): IChatConversation {
        const currentUserId = this.userDataService.getUserId();
        // array should be sorted for compare with lodash
        const userIdsToFind: number[] = union(selectedUserIds, [currentUserId]).sort();

        return this.fiChatSyncService.conversations.find((conversation) => {
            const userIds = conversation.Users.map((x) => x.Id).sort();
            return conversation.Type === conversationType && isEqual(userIds, userIdsToFind);
        });
    }

    getNewConversationInstance(user?: ILightWeightUser): IChatConversation {
        return {
            Id: 0,
            NotificationCategory: PersonalNotificationsCategoryType.Visited, // we treat new current user's conversation as visited from start
            IsHidden: false,
            Name: this.appTextService.get('chat.new_message'),
            Type: undefined,
            LatestActivity: undefined,
            LatestActivityMessage: this.getNewConversationMessageInstance(undefined, undefined),
            Users: this.getNewConversationUsers(user),
            IsDefaultName: true,
        };
    }

    getForwardMessageConversationInstance(): IChatConversation {
        return {
            Id: 0,
            NotificationCategory: PersonalNotificationsCategoryType.Visited, // we treat new current user's conversation as visited from start
            IsHidden: false,
            Name: this.appTextService.get('message.forward_to') + '...',
            Type: FiChatConversationType.Forward,
            LatestActivity: undefined,
            LatestActivityMessage: this.getNewConversationMessageInstance(undefined, undefined),
            Users: [],
            IsDefaultName: true,
        };
    }

    getNewConversationMessageInstance(content?: string, conversationMessageType?: FiChatConversationMessageType) {
        const message: IChatConversationMessage = {
            Id: 0,
            ConversationId: undefined,
            Type: isDefined(conversationMessageType) ? conversationMessageType : FiChatConversationMessageType.Common,
            Content: content,
            UserProfile: this.userDataService.getUserProfile(),
            UserFirstName: this.userDataService.getUserFirstName(),
            NotificationCategory: PersonalNotificationsCategoryType.Visited, // we treat new current user's message as visited from start
            HasEditAccess: true,
            HasDeleteAccess: true,
            MediaBankFileReferences: [],
            Reactions: [],
            Links: [],
            TargetUsers: [],
            ReadBy: [],
            IsHidden: false,
        };
        return message;
    }

    isConversationNew(conversation: IChatConversation) {
        return !conversation.Id;
    }

    isGroupChat(conversation: IChatConversation): boolean {
        return conversation.Type === FiChatConversationType.Group;
    }

    isPersonalChat(conversation: IChatConversation): boolean {
        return conversation.Type === FiChatConversationType.OneToOne;
    }

    getDaysDifference(date: string): number {
        const conversationDate = new Date(date).getTime();
        const today = new Date().getTime();
        const diffTime = Math.abs(today - conversationDate);
        return Math.floor(diffTime / MSPerDay);
    }

    isMessageEdited(createdOn: string, changedOn: string): boolean {
        return new Date(createdOn).getTime() < new Date(changedOn).getTime();
    }

    filterChatConversations(conversations: IChatConversation[], searchValue: string) {
        if (!searchValue) return conversations;

        const lowerCaseSearchValue = searchValue.toLowerCase();

        return conversations.filter((conversation) => {
            if (conversation.Name && conversation.Name.toLowerCase().includes(lowerCaseSearchValue)) {
                return true;
            }
            const searchUserFound: boolean = conversation.Users.some((conversationUser) => {
                const userFullName = conversationUser.FullName || '';
                const userPreferredBusinessUnitName = conversationUser.PreferredBusinessUnitName || '';
                return (
                    (userFullName.toLowerCase().includes(lowerCaseSearchValue) ||
                        userPreferredBusinessUnitName.toLowerCase().includes(lowerCaseSearchValue)) &&
                    !this.isCurrentUser(conversationUser.Id)
                );
            });
            return searchUserFound;
        });
    }

    public increaseVisibleSuggestionLimit(isUnitSuggestionNotFullyLoaded: boolean): void {
        isUnitSuggestionNotFullyLoaded
            ? (this._visibleUnitSuggestionLimit += this._suggestionsOffsetStep)
            : (this._visibleOtherSuggestionLimit += this._suggestionsOffsetStep);
    }

    public resetVisibleSuggestionCounter(): void {
        this._visibleUnitSuggestionLimit = this._suggestionsOffsetStep;
        this._visibleOtherSuggestionLimit = this._suggestionsOffsetStep;
    }

    public filterChatSuggestions(
        suggestions: IChatSuggestion[],
        searchValue: string,
        isUnitNotFullyLoaded: boolean,
    ): IChatSuggestion[] {
        const tempSuggestions: IChatSuggestion[] = cloneDeep(suggestions);
        return tempSuggestions.filter((suggestion: IChatSuggestion, index: number) => {
            if (!!searchValue) {
                suggestion.Users = suggestion.Users.filter((user: ILightWeightUser) => {
                    return (
                        user.FullName.toLowerCase().includes(searchValue.toLowerCase()) ||
                        user.PreferredBusinessUnitName.toLowerCase().includes(searchValue.toLowerCase())
                    );
                });
            }
            if (index === 0) {
                if (isUnitNotFullyLoaded && suggestion.Users.length > this._visibleUnitSuggestionLimit)
                    suggestion.Users.length = this._visibleUnitSuggestionLimit;
                return !!suggestion.Users.length;
            }

            if (suggestion.Users.length > this._visibleOtherSuggestionLimit)
                suggestion.Users.length = this._visibleOtherSuggestionLimit;
            return !!suggestion.Users.length;
        });
    }

    getHoursDifference(mainDate: string, previousDate: string) {
        const firstMills = new Date(mainDate).getTime();
        const secondMills = new Date(previousDate).getTime();
        const diffTime = Math.abs(secondMills - firstMills);
        return diffTime / MSPerHour;
    }

    //endregion

    isCurrentUser(conversationUserId: number): boolean {
        return conversationUserId === this.userDataService.getUserId();
    }

    isSystemMessage(messageType: FiChatConversationMessageType): boolean {
        return messageType !== FiChatConversationMessageType.Common;
    }

    isOnlySourceMessage(conversationMessage: IChatConversationMessage): boolean {
        return (
            !conversationMessage.Content?.length &&
            !conversationMessage.Links?.length &&
            !conversationMessage.MediaBankFileReferences?.length
        );
    }

    getSystemMessage(conversationMessage: IChatConversationMessage): string {
        switch (conversationMessage.Type) {
            case FiChatConversationMessageType.GroupRename:
                return this.appTextService.get('chat.group_renamed_system_message', {
                    '{0}': conversationMessage.Content,
                });
            case FiChatConversationMessageType.Deleted:
                return this.appTextService.get('chat.message_removed');
            case FiChatConversationMessageType.GroupAddUser:
                return this.appTextService.get(
                    conversationMessage.TargetUsers.length > 1 || this.isTargetUserSingleAndCurrent(conversationMessage)
                        ? 'chat.group_user_added_multiple'
                        : 'chat.group_user_added',
                );
            case FiChatConversationMessageType.GroupLeave:
                return this.appTextService.get('chat.group_leave');
            case FiChatConversationMessageType.GroupRemoveUser:
                return this.appTextService.get(
                    conversationMessage.TargetUsers.length > 1 || this.isTargetUserSingleAndCurrent(conversationMessage)
                        ? 'chat.group_user_removed_multiple'
                        : 'chat.group_user_removed',
                );
            case FiChatConversationMessageType.GroupStart:
                return this.appTextService.get('chat.group_created');

            case FiChatConversationMessageType.GroupStart:
                return this.appTextService.get('chat.group_created');

            case FiChatConversationMessageType.UserDeleted:
                return this.getDeletedSystemMessageText(conversationMessage);
        }
    }

    private getDeletedSystemMessageText(conversationMessage: IChatConversationMessage): string {
        let deletedUserName: string;
        if (conversationMessage.TargetUsers.length) {
            deletedUserName = conversationMessage.TargetUsers[0].FullName;
        } else {
            deletedUserName = 'user';
            this.fiLogErrorService.logError(`no TargetUsers in getDeletedSystemMessageText`);
        }
        return this.appTextService.get('chat.user_deleted_group', {
            '{user_name}': deletedUserName,
        });
    }

    private isTargetUserSingleAndCurrent(conversationMessage: IChatConversationMessage) {
        return (
            conversationMessage.TargetUsers.length === 1 && this.isCurrentUser(conversationMessage.TargetUsers[0].Id)
        );
    }

    isCurrentUserAddedToGroup(conversationMessage: IChatConversationMessage) {
        return (
            conversationMessage.TargetUsers.length === 1 &&
            this.isCurrentUser(conversationMessage.TargetUsers[0].Id) &&
            conversationMessage.Type === FiChatConversationMessageType.GroupAddUser
        );
    }

    isCurrentUserRenamedGroup(conversationMessage: IChatConversationMessage) {
        return (
            this.isCurrentUser(conversationMessage.ChangedByUserId) &&
            conversationMessage.Type === FiChatConversationMessageType.GroupRename
        );
    }

    getLatestSystemMessage(conversationMessage: IChatConversationMessage): string {
        switch (conversationMessage.Type) {
            case FiChatConversationMessageType.GroupRename: {
                const targetString = this.isCurrentUserRenamedGroup(conversationMessage)
                    ? this.appTextService.get('common.you')
                    : this.getCreatedByUserName(conversationMessage);

                return (
                    targetString +
                    ' ' +
                    this.appTextService.get('chat.group_renamed_system_message', {
                        '{0}': conversationMessage.Content,
                    })
                );
            }

            case FiChatConversationMessageType.Deleted:
                const userName: string =
                    this.userDataService.getUserId() === conversationMessage.CreatedByUserId
                        ? this.appTextService.get('common.you')
                        : this.getCreatedByUserName(conversationMessage);
                return this.appTextService.get('chat.message_removed', { '{0}': userName });

            case FiChatConversationMessageType.GroupAddUser:
                return this.getLatestSystemMessageWithTaggedUsers(
                    conversationMessage,
                    'chat.group_user_added_multiple',
                    'chat.group_user_added',
                );

            case FiChatConversationMessageType.GroupRemoveUser:
                return this.getLatestSystemMessageWithTaggedUsers(
                    conversationMessage,
                    'chat.group_user_removed_multiple',
                    'chat.group_user_removed',
                );

            case FiChatConversationMessageType.GroupLeave:
                return (
                    this.getCreatedByUserName(conversationMessage) + ' ' + this.appTextService.get('chat.group_leave')
                );

            case FiChatConversationMessageType.GroupStart:
                return (
                    this.getCreatedByUserName(conversationMessage) + ' ' + this.appTextService.get('chat.group_created')
                );

            case FiChatConversationMessageType.UserDeleted:
                return this.getDeletedSystemMessageText(conversationMessage);
        }
    }

    openRemoveConversationDialog(): Promise<any> {
        return new Promise<void>((resolve, reject) => {
            this.fiDialogsService
                .confirm({
                    headerText: this.appTextService.get('chat.confirm_remove_conversation_header'),
                    messageText: this.appTextService.get('chat.confirm_remove_conversation_message'),
                    yesButtonText: this.appTextService.get('common.delete'),
                })
                .onYesButtonClick(() => resolve())
                .onNoButtonClick(reject);
        });
    }

    openLeaveGroupConversationDialog() {
        return new Promise<void>((resolve, reject) => {
            this.fiDialogsService
                .confirm({
                    headerText: this.appTextService.get('chat.confirm_leave_group_conversation_header'),
                    messageText: this.appTextService.get('chat.confirm_leave_group_conversation_message'),
                    yesButtonText: this.appTextService.get('common.leave'),
                })
                .onYesButtonClick(() => resolve())
                .onNoButtonClick(reject);
        });
    }

    openRenameConversationDialog(conversation: IChatConversation): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            this.fiDialogsService
                .custom({
                    headerText: this.appTextService.get('chat.rename_group'),
                    yesButtonText: this.appTextService.get('common.save'),
                    noButtonText: this.appTextService.get('common.cancel'),
                    contentComponentRef: FiChatConversationRenameDialogComponent,
                    contentComponentData: conversation,
                    config: {
                        width: '300px',
                        minWidth: '300px',
                    },
                })
                .onYesButtonClick((result) => resolve(result))
                .onNoButtonClick(reject);
        });
    }

    hasAttachments(conversationMessage: IChatConversationMessage): boolean {
        return !!conversationMessage.MediaBankFileReferences?.length || !!conversationMessage.Links?.length;
    }

    getMessageAttachmentExtension(message: IChatConversationMessage) {
        if (!!message.MediaBankFileReferences.length)
            return message.MediaBankFileReferences[0].Extension.replace('.', '').toUpperCase();
        if (!!message.Links.length) return this.appTextService.get('common.gif');
    }

    getMessageContent(messageContent: string): SafeHtml | string {
        return messageContent
            ? this.sanitizer.bypassSecurityTrustHtml(messageContent.replace(/\\/g, '').replace(/&quot;/g, `'`))
            : '';
    }

    getCreatedByUserName(message: IChatConversationMessage): string {
        return message.UserProfile?.FullName ?? message.CreatedByUserName;
    }

    private getNewConversationUsers(user: ILightWeightUser): IChatConversationUser[] {
        if (user) {
            return [
                {
                    Id: user.Id,
                    FullName: user.FullName,
                    MediaBankPhysicalFileName: user.MediaBankPhysicalFileName,
                    MediaBankExtension: user.MediaBankExtension,
                    PreferredBusinessUnitName: user.PreferredBusinessUnitName,
                },
            ];
        }
        return [];
    }

    private getLatestSystemMessageWithTaggedUsers(
        conversationMessage: IChatConversationMessage,
        pluralTextId: string,
        singleTextId: string,
    ): string {
        const targetUsers = conversationMessage.TargetUsers;
        if (!targetUsers.length) return '';

        const currentUserId = this.userDataService.getUserId();
        const textId =
            targetUsers.length > 1 || targetUsers.find((u) => u.Id == currentUserId)
                ? pluralTextId // were added (only You or multiple users)
                : singleTextId; // was added (one other user)

        const targetUserNames = targetUsers.map((user, idx) => {
            if (user.Id === currentUserId) {
                const youString = this.appTextService.get('common.you');
                return idx === 0 ? youString : youString.toLowerCase();
            }
            return user.FullName;
        });
        const targetString = targetUserNames.join(', ');
        return (
            targetString + ' ' + this.appTextService.get(textId) + ' ' + this.getCreatedByUserName(conversationMessage)
        );
    }
}
